import { httpClient as http } from '@/utils/http'
import type { AxiosRequestConfig } from 'axios'

const API_BASE = '/mdm/route-v2'
export const routeV2SilentErrorConfig: AxiosRequestConfig & { showErrorMessage: false } = { showErrorMessage: false }

export type RouteV2Id = string | number

export interface PageResultVO<T> {
  records: T[]
  total: number
  pageNum: number
  pageSize: number
}

export interface RouteV2QueryRequest {
  pageNum: number
  pageSize: number
  keyword?: string
  routeTypes?: number[] | null
  routeLevel?: number
  parentId?: RouteV2Id
  enabled?: number
  dataSource?: number
  tenantCopied?: boolean
  routeNameShortNotEmpty?: boolean
  excludePlatformIds?: RouteV2Id[]
}

export interface RouteV2SaveRequest {
  id?: RouteV2Id
  routeTypes?: number[] | null
  routeLevel: number
  parentId: RouteV2Id
  routeNameCn: string
  routeNameEn: string
  routeNameShort: string
  enabled: number
}

export interface RouteV2VO {
  id: RouteV2Id
  tenantId?: RouteV2Id
  routeUuid?: string
  routeTypes?: number[] | null
  routeLevel: number
  parentId: RouteV2Id
  parentNameCn?: string
  parentNameEn?: string
  routeNameCn: string
  routeNameEn: string
  routeNameShort?: string
  enabled: number
  dataSource?: number
  editable?: boolean
  statusEditable?: boolean
  tenantCopied?: boolean
  dateCreated?: string
  dateModified?: string
}

export interface RouteV2ImportErrorVO {
  rowNumber: number
  field: string
  reason: string
}

export interface RouteV2ImportResultVO {
  totalRows: number
  successRows: number
  createdCount: number
  failedRows: number
  errors: RouteV2ImportErrorVO[]
}

export type PlatformCopySelectionMode = 'IDS' | 'QUERY'
export type PlatformCopyMode = 'SYNC' | 'TASK'

export interface RouteV2PlatformCopyRequest {
  selectionMode: PlatformCopySelectionMode
  platformIds?: RouteV2Id[]
  query?: RouteV2QueryRequest
  excludePlatformIds?: RouteV2Id[]
}

export interface PlatformRouteRelationSyncRequest {
  tenantId?: RouteV2Id
  routeNameShorts: string[]
}

export interface MatchRouteByPortRequest {
  /** 起运港ID */
  pol: string
  /** 目的港ID */
  pod: string
  /** 航线类型：0-海运，1-空运；不传时不按类型过滤 */
  routeType?: number
}

export interface PlatformCopySubmitVO {
  copyMode: PlatformCopyMode
  matchedCount?: number
  requestedCount?: number
  excludedCount?: number
  copiedCount?: number
  skippedCount?: number
  failedCount?: number
  errorMessage?: string
  taskRecordId?: RouteV2Id
  status?: number
  existingActiveTask?: boolean
}

export enum RouteV2Type {
  SEA = 0,
  AIR = 1
}

export enum RouteV2Level {
  FIRST = 1,
  SECOND = 2,
  THIRD = 3
}

export enum RouteV2EnabledStatus {
  DISABLED = 0,
  ENABLED = 1
}

export const routeV2Api = {
  queryPage: (params: RouteV2QueryRequest, config?: AxiosRequestConfig) => {
    return http.post<PageResultVO<RouteV2VO>>(`${API_BASE}/queryPage`, params, config)
  },

  batchQueryByIds: (ids: RouteV2Id[]) => {
    return http.post<Map<RouteV2Id, RouteV2VO>>(`${API_BASE}/batch-query-by-ids`, ids)
  },

  save: (params: RouteV2SaveRequest) => {
    return http.post<RouteV2Id>(`${API_BASE}/save`, params, routeV2SilentErrorConfig)
  },

  updateStatus: (ids: RouteV2Id[], enabled: number) => {
    return http.post<boolean>(`${API_BASE}/updateStatus`, { ids, enabled })
  },

  queryPlatformPage: (params: RouteV2QueryRequest) => {
    return http.post<PageResultVO<RouteV2VO>>(`${API_BASE}/queryPlatformPage`, params)
  },

  batchReference: (platformIds: RouteV2Id[]) => {
    return http.post<boolean>(`${API_BASE}/batchReference`, { platformIds })
  },

  copyPlatform: (params: RouteV2PlatformCopyRequest) => {
    return http.post<PlatformCopySubmitVO>(`${API_BASE}/copyPlatform`, params)
  },

  syncPlatformRouteRelation: (params: PlatformRouteRelationSyncRequest) => {
    return http.post<PlatformCopySubmitVO>('/mdm/port-route-v2-relation/sync/platform-route-relation', params)
  },

  matchRouteByPortId: (params: MatchRouteByPortRequest): Promise<string[]> => {
    return http.post<any, string[]>(`${API_BASE}/matchRouteByPortId`, params)
  }
}
